// Card 63, the memorial reef (R964, ui-map-interface.md §17.3.63).
//
// A world remembers who won it. At every season roll the hub appends the
// first commander of the closed season to the world's winner list, and the
// map generation of the next season builds the winner of the LAST season a
// memorial reef: three cells of rock in a crescent in open water
// (`numbers.md` §14.1). This piece names that reef.
//
// **THE ROCK IS NOT DRAWN HERE.** The three cells are rock in
// `Hello.blocked`, so the terrain layer draws the stone, the rims and the
// contour rings for nothing, and a client that draws no furniture still
// sails round the reef. This piece adds the MARKS and the NAME alone: a
// hex of rock in open water is a hex of rock until the chart says whose it
// is.
//
// **THE NAME IS A PLACE NAME.** The historical title rides the arc of
// `place-names.ts` (§17.3.19), which is one circle of slight, uniform
// curvature over the crown of the cluster. It is the same mechanism and not
// a second one, because a name over a body of water bulges one way in this
// chart.
//
// **NOT A BASE.** The piece answers no `pick`. No press changes the world at
// a monument, no hull docks at one, and no claim grows from one.
//
// **THE COST IS THREE CELLS.** A map carries one reef, and the pack is
// three coral glyphs or fourteen dashed hex edges plus two short runs. The
// piece packs on a hello, on a reading and on a zoom band, and costs one
// flush a frame after that.

import { chart as S } from '../../../gen/strings';
import { star } from '../../../render/chart-glyphs';
import { toRoman } from '../../../render/roman';
import { polylineLength, runWidth } from '../../../render/text-run';
import { MonumentKind, type Monument } from '../../../gen/proto/aquaphobia';
import { cellCenter, hexCorner, mulberry32, neighborAt } from '../../../world';
import type { SegmentSink } from '../../layers/batches';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { uniformArc } from './place-names';
import { CHART_INK, faceOf, inkOf } from './shared';

/** One outer hex edge of the cluster, in world px. */
interface Edge { ax: number; ay: number; bx: number; by: number }

/** One reef, as the last pack read it. */
interface Reef {
  /** The three cell centres, in world px. */
  cells: { x: number; y: number }[];
  /** The outer hex edges of the cluster: the crescent's own outline. */
  edges: Edge[];
  /**
   * The middle of the cluster, and the top and the bottom of its OUTLINE.
   *
   * The two straight lines stand clear of the stone and not clear of the
   * cell centres, so the rim of the crescent is what they measure from.
   */
  cx: number;
  cy: number;
  top: number;
  bottom: number;
  /** The winner's name as the season closed, and the season she won. */
  commander: string;
  season: number;
}

/**
 * The outer hex edges of a set of cells: every edge whose neighbour is not
 * in the set.
 *
 * Direction `k` faces the edge between corner `k` and corner `k+1`
 * (`world.ts`), so one walk of the six directions gives the whole outline.
 * A connected crescent of three cells shares two edges, so it has fourteen.
 */
function outerEdges(cells: readonly number[]): Edge[] {
  const set = new Set(cells);
  const out: Edge[] = [];
  for (const cell of cells) {
    const [cx, cy] = cellCenter(cell);
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(cell, k);
      if (n >= 0 && set.has(n)) continue;
      const [ax, ay] = hexCorner(k);
      const [bx, by] = hexCorner(k + 1);
      out.push({ ax: cx + ax, ay: cy + ay, bx: cx + bx, by: cy + by });
    }
  }
  return out;
}

/**
 * One engraved coral: a branching line that grows upward from (x, y).
 *
 * The count is arithmetic and not a measurement: a binary branch of
 * `depth` orders is `2^depth − 1` segments, whatever the seed. The seed
 * turns the branch angles alone, so two corals of one reef differ and a
 * reload draws the same two.
 */
function coral(
  seg: SegmentSink, x: number, y: number, seed: number,
  color: number, alpha: number, width: number, len: number, depth: number,
): void {
  const rnd = mulberry32(seed);
  const branch = (bx: number, by: number, ang: number, l: number, d: number): void => {
    if (d <= 0) return;
    const x1 = bx + Math.cos(ang) * l, y1 = by + Math.sin(ang) * l;
    seg.line(bx, by, x1, y1, width * (0.5 + d * 0.25), color, alpha);
    branch(x1, y1, ang - 0.45 - rnd() * 0.5, l * 0.68, d - 1);
    branch(x1, y1, ang + 0.45 + rnd() * 0.5, l * 0.68, d - 1);
  };
  branch(x, y, -Math.PI / 2, len, depth);
}

/** How many dashes one edge of `len` px carries, in the pack's pattern. */
export function dashesOn(len: number, dash: readonly number[]): number {
  const period = (dash[0] ?? 0) + (dash[1] ?? 0);
  if (!(period > 0)) return 1;
  return Math.max(1, Math.round(len / period));
}

export class MemorialReefPiece implements Piece {
  readonly id = 'memorial_reef' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /**
   * The reef was laid before this season opened, so it is the sea the
   * fight happened in and a replay film keeps it.
   */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'font'] as const;

  /** The reefs of the last pack. The tests read them. */
  reefs: Reef[] = [];

  visible(ctx: ChartContext): boolean {
    return ctx.state.monuments.some(isReef);
  }

  rebuild(ctx: ChartContext): void {
    this.reefs = [];
    for (const m of ctx.state.monuments) {
      if (!isReef(m) || m.cells.length === 0) continue;
      const cells = m.cells.map((c) => {
        const [x, y] = cellCenter(c);
        return { x, y };
      });
      const edges = outerEdges(m.cells);
      let cx = 0, cy = 0, top = Infinity, bottom = -Infinity;
      for (const c of cells) { cx += c.x / cells.length; cy += c.y / cells.length; }
      for (const e of edges) {
        top = Math.min(top, e.ay, e.by);
        bottom = Math.max(bottom, e.ay, e.by);
      }
      this.reefs.push({
        cells,
        edges,
        cx, cy, top, bottom,
        commander: m.commander,
        season: m.season,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.memorial_reef;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    const marks = t.fig.alpha * r.alpha;
    for (const reef of this.reefs) {
      const sub = champion(ctx, reef.season, hist);
      if (hist) {
        const f = t.historical.fig;
        // The stone's own ink: the brightest of the four ridge rings, so
        // the coral reads as a thing cut into the rock and not as a thing
        // laid over it (§6.4 wants texture, not spectacle).
        const stone = inkOf(r.ink, CHART_INK.ridge);
        reef.cells.forEach((c, i) => coral(
          sinks.segments, c.x, c.y + f.coral_dy, i + 1,
          stone, marks, r.width, f.coral_len, f.coral_depth,
        ));
        const title = S.reef_title_historical({ name: reef.commander });
        this.arc(sinks, ctx, reef, title, ink, r.alpha);
        ctx.glyphs.word(
          sinks.quads, sub, reef.cx, reef.bottom + t.fig.line_dy,
          face, t.text.sub, CHART_INK.dim, r.alpha, { anchor: 'centre' },
        );
        continue;
      }
      // The dotted danger line of the modern reading runs the outline of
      // the crescent, so a reader who knows the chart reads "do not cross"
      // before she reads the name.
      for (const e of reef.edges) dashes(sinks.segments, e, r.dash, ink, marks, r.width);
      const f = t.modern.fig;
      for (const c of reef.cells) {
        // The INT 1 coral mark is three crossing strokes, which is the
        // glyph card 26 draws for a rock awash: one shape, one owner.
        star(sinks.segments, c.x, c.y, f.mark_r, ink, f.mark_alpha, r.width);
      }
      ctx.glyphs.word(
        sinks.quads, S.reef_coral, reef.cx, reef.cy,
        face, t.text.tag, ink, f.tag_alpha, { anchor: 'centre' },
      );
      const head = reef.top - t.fig.line_dy;
      // The two lines are one block, so they grow about one pivot.
      const pivot = { x: reef.cx, y: head };
      ctx.glyphs.word(
        sinks.quads, S.reef_title_modern({ name: reef.commander }),
        reef.cx, head, face, t.text.main, ink, r.alpha, { anchor: 'centre', pivot },
      );
      ctx.glyphs.word(
        sinks.quads, sub, reef.cx, head + t.fig.line_h,
        face, t.text.sub, CHART_INK.dim, r.alpha, { anchor: 'centre', pivot },
      );
    }
  }

  /** The historical title, laid on the arc of §17.3.19 over the crown. */
  private arc(
    sinks: ChartSinks, ctx: ChartContext, reef: Reef,
    title: string, ink: number, alpha: number,
  ): void {
    const t = ctx.cfg.pieces.memorial_reef;
    const f = t.historical.fig;
    const face = faceOf('historical');
    // The rim the arc takes its crown from is the outline lifted clear of
    // the stone, so the name never lies on the rock it names.
    const rim: [number, number][] = [];
    for (const e of reef.edges) {
      rim.push([e.ax, e.ay - t.fig.arc_offset], [e.bx, e.by - t.fig.arc_offset]);
    }
    if (rim.length === 0) return;
    const len = runWidth(
      title, ctx.glyphs.stepMetrics(face, t.text.main), f.letter_spacing,
    );
    const pts = uniformArc(rim, len, t.fig.curve_deg, t.fig.arc_step_px);
    ctx.glyphs.wordAlong(sinks.quads, title, pts, face, t.text.main, ink, alpha, {
      anchor: 'centre',
      startS: polylineLength(pts, false) / 2,
      closed: false,
      letterSpacing: f.letter_spacing,
    });
  }
}

/** Is this monument a reef? */
function isReef(m: Monument): boolean {
  return m.kind === MonumentKind.MONUMENT_KIND_REEF;
}

/**
 * The champion line both monuments write (R964).
 *
 * The world name is the hello's, and the hub sends none on an arcade
 * world, so the shorter form stands in there. The historical reading
 * counts the season in Roman figures, as every historical figure of this
 * chart does.
 */
export function champion(ctx: ChartContext, season: number, hist: boolean): string {
  const world = ctx.state.hello?.worldName ?? '';
  if (world === '') return seasonChampion(season, hist);
  const s = hist ? toRoman(season) : String(season);
  return hist
    ? S.monument_champion_world_historical({ world, season: s })
    : S.monument_champion_world_modern({ world, season: s });
}

/**
 * The champion line that names the season alone. A pool monument names
 * another world, so piece 65 always writes this form (R1248).
 */
export function seasonChampion(season: number, hist: boolean): string {
  return hist
    ? S.monument_champion_historical({ season: toRoman(season) })
    : S.monument_champion_modern({ season: String(season) });
}

/** One dashed hex edge, in the pattern the pack states. */
function dashes(
  seg: SegmentSink, e: Edge, dash: readonly number[],
  color: number, alpha: number, width: number,
): void {
  const len = Math.hypot(e.bx - e.ax, e.by - e.ay);
  const n = dashesOn(len, dash);
  const period = (dash[0] ?? 0) + (dash[1] ?? 0);
  const on = period > 0 ? (dash[0] ?? 0) / period : 1;
  for (let i = 0; i < n; i++) {
    const t0 = i / n, t1 = t0 + on / n;
    seg.line(
      e.ax + (e.bx - e.ax) * t0, e.ay + (e.by - e.ay) * t0,
      e.ax + (e.bx - e.ax) * t1, e.ay + (e.by - e.ay) * t1,
      width, color, alpha,
    );
  }
}

/** The one instance the registry holds. */
export const memorialReef = new MemorialReefPiece();
